/**
 * Shared conflict-merge priority used by both availability stores' `checkResourceConflicts`.
 *
 * When several slots / days contribute an availability entry for the same resource, the most severe
 * one must win the single slot in the result map:
 *   1. a hard block always overrides whatever is stored,
 *   2. otherwise a newer entry replaces a stored "maybe" (upgrade),
 *   3. otherwise it only fills in when nothing blocking is recorded yet.
 *
 * The time-grid store (`TimeAvailabilityStore`) and the teaching-block day-grid store
 * (`TeachingBlockDayAvailabilityStore`) keep otherwise distinct algorithms — different key schemes,
 * slot-overlap handling, iteration strategy — but this priority step was copy-pasted identically
 * across both for the room/teacher/subject branches. Extracting it keeps that decision in one place
 * so the two stores cannot silently drift apart.
 */
export function mergeConflictEntry<E extends { maybe: boolean; blocked: boolean }>(
  cardAvailability: Map<string, E>,
  key: string,
  entry: E,
): void {
  const current = cardAvailability.get(key);
  if (entry.blocked) {
    cardAvailability.set(key, entry);
  } else if (current?.maybe) {
    cardAvailability.set(key, entry);
  } else if (!current?.blocked) {
    cardAvailability.set(key, entry);
  }
}
